import React, { useEffect, useState } from 'react';
import ComplianceStatus from './ComplianceStatus';
import DeviceBreakedown from './DeviceBreakedown';
import GridPage from '../../components/gridcomponent/gridPage';
import RectangleRow from '../../components/RectangleRow/rectangleRow';
import Combo from '../../components/Combo';
import DeviceHealth from './DeviceHealth';
import PachBySeverity from './PachBySeverity';
import Header from '../../components/header';
import { useAuth } from 'oidc-react';
import Project from '../../common/Utils/project';
import { generateGuid } from '../../common/Utils/guid';
import { Copy } from 'lucide-react';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import api from '../../components/services/apiService';
import { useSignalR } from '../../providers/SignalRProvider/SignalRProvider';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import DashboardReportFloatingButton from '../../components/Button/DashboardReportFloatingButton';
import Button from '../../components/Button'; // Adjust the import path as necessary
import { getPatchBySeverityConfiguration, PatchBySeverityConfiguration } from '../../components/services/TenantSettingsService';
import { usePermissions } from '../../hooks/usePermissions';

const EMPTY_PATCH_SEVERITY_CONFIG: PatchBySeverityConfiguration = {
  severityCVEMapping: [],
  daysRange: [],
};

const DashboardPage = () => {
  const userName = localStorage.getItem('userName');
  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();
  const { canEdit } = usePermissions();
  const canExportReport = canEdit('report');

  // --- SIGNALR hooks ---
  const { isConnected, generateReport, registerListener, unregisterListener, connectNow } = useSignalR();
  const { addToast } = useToast();

  const [allGroupsData, setAllGroupsData] = useState<any[]>([]);
  const [groupID, setGroupID] = useState<string | null>(null);
  const [groupName, setGroupName] = useState<string | null>(null);
  const [selectedGroupData, setSelectedGroupData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [patchBySeverityConfig, setPatchBySeverityConfig] = useState<PatchBySeverityConfiguration>(EMPTY_PATCH_SEVERITY_CONFIG);

  // --- Modal State ---
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const handleGetGroupName = (name: string | null) => {
    setGroupName(name);
    return name ?? '';
  };

  // Fetch Dashboard Data
  const getDashboardData = async () => {
    if (!selectedTenantId) {
      setAllGroupsData([]);
      setSelectedGroupData(null);
      setPatchBySeverityConfig(EMPTY_PATCH_SEVERITY_CONFIG);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      if (auth && auth.userData) {
        const tenantId = selectedTenantId;

        const [dashboardResponse, patchBySeverityConfiguration] = await Promise.all([
          api.get(`${Project.api}dashboardservice/Dashboard/${tenantId}`),
          getPatchBySeverityConfiguration(tenantId as string).catch((error) => {
            console.error('Error fetching patch-by-severity settings:', error);
            return EMPTY_PATCH_SEVERITY_CONFIG;
          }),
        ]);

        setPatchBySeverityConfig(patchBySeverityConfiguration);

        let { data } = dashboardResponse;
        let groups = Array.isArray(data) ? data : [data];

        setAllGroupsData(groups);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setAllGroupsData([]);
      setPatchBySeverityConfig(EMPTY_PATCH_SEVERITY_CONFIG);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDashboardData();
  }, [selectedTenantId]);

  // Select Group Logic (unchanged)
  // In DashboardPage.tsx, replace the aggregation logic in the useEffect with this:

  useEffect(() => {
    if (allGroupsData.length === 0) return;

    if (groupID) {
      // GROUP MODE
      const selected = allGroupsData.find((g) => String(g.tenantGroupId) === String(groupID));
      setSelectedGroupData(selected || null);
    } else {
      // TENANT MODE — Use null group but merge agent data from all groups
      let nullGroup = allGroupsData.find((g) => g.tenantGroupId === null);

      if (nullGroup?.criticalSeverityPatches?.osUpdates) {
        // Collect agent data from all individual groups
        const agentDataMap = new Map<string, any[]>();

        allGroupsData.forEach((group) => {
          if (group.tenantGroupId !== null && group.criticalSeverityPatches?.osUpdates) {
            group.criticalSeverityPatches.osUpdates.forEach((osUpdate: any) => {
              const updateId = osUpdate.updateId;
              const additionalInfo = osUpdate.additionalInfo || {};

              if (additionalInfo[updateId] && Array.isArray(additionalInfo[updateId])) {
                if (!agentDataMap.has(updateId)) {
                  agentDataMap.set(updateId, []);
                }
                agentDataMap.get(updateId)!.push(...additionalInfo[updateId]);
              }
            });
          }
        });

        // Update osUpdates with collected agent data
        nullGroup = {
          ...nullGroup,
          criticalSeverityPatches: {
            ...nullGroup.criticalSeverityPatches,
            osUpdates: nullGroup.criticalSeverityPatches.osUpdates.map((osUpdate: any) => {
              const updateId = osUpdate.updateId;
              const agentList = agentDataMap.get(updateId) || [];

              return {
                ...osUpdate,
                additionalInfo: {
                  [updateId]: agentList,
                },
              };
            }),
          },
        };
      }

      setSelectedGroupData(nullGroup || null);
    }
  }, [groupID, allGroupsData]);

  // --- Download Report Handler ---
  const downloadReport = (url: string) => {
    const res = api.get(url, {
      responseType: 'blob',
    });
    res
      .then((response) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(new Blob([response.data]));
        link.setAttribute('download', `${groupName || 'dashboard'}report.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);

        // Toast on successful download
        addToast('Report downloaded successfully!', 'success', 3000, 'Download Complete');

        // Reset the button state after download
        handleReset();
      })
      .catch((error) => {
        console.error('Error downloading report:', error);
        addToast('Failed to download report', 'error', 3000, 'Download Error');
      });
  };

  // --- EXPORT HANDLER ---
  const handleExportDashboard = async (groupId: string) => {
    setIsExporting(true);

    if (!isConnected) {
      try {
        await connectNow();
      } catch {
        setIsExporting(false);
        addToast('Unable to connect for live status updates. Please try again in a moment.', 'error', 4000, 'Connection Error');
        return;
      }
    }

    try {
      const msg = await generateReport(groupId);
      addToast('Export started! You will be notified when ready.', 'info', 5000, 'Export Started');
    } catch {
      setIsExporting(false);
      addToast('Failed to start export', 'error', 3000, 'Error');
    }
  };

  const handleReset = () => {
    setIsExporting(false);
    setIsReady(false);
    setReportData(null);
    setReportModalOpen(false);
  };

  // --- SIGNALR LISTENER ---
  useEffect(() => {
    const listener = (job: any) => {
      // Reset exporting state regardless of success or failure
      setIsExporting(false);

      if (job.isSuccessful) {
        setReportData(job);
        setReportModalOpen(true);
      }
    };

    registerListener(listener);

    return () => {
      if (window.__sharedSignalRListeners) {
        window.__sharedSignalRListeners.delete(listener);
      }
      unregisterListener();
    };
  }, [registerListener, unregisterListener]);

  return (
    <div className="flex-1">
      <Header title="Dashboard Overview" dropdown={1} setGroupID={setGroupID} getGroupName={handleGetGroupName} />
      <div className="p-6 flex-1">
        <div className="flex justify-between">
          <div className="text-4xl flex items-center">
            <span className="font-extrabold">Hi</span>
            <span className="font-bold text-[#C72727] ml-2 mr-1">{userName || 'User'}</span>
            <span className="font-extrabold">, here's your current compliance snapshot</span>
          </div>
          <div className="flex items-center">
            {canExportReport && (
              <div className="flex items-center">
                <Button
                  onClick={() => handleExportDashboard(groupID as string)}
                  disabled={isExporting}
                  isLoading={isExporting}
                  label={isExporting ? 'Exporting…' : 'Export Dashboard'}
                />
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-gray-500 text-center mt-6">Loading dashboard...</div>
        ) : selectedGroupData ? (
          <>
            {/* Compliance and Device Breakdown */}
            <div className="flex mt-[20px] gap-[20px]">
              {selectedGroupData.compliancesInfo && <ComplianceStatus compliancesInfo={selectedGroupData.compliancesInfo} />}
              {selectedGroupData.compliancesInfo && (
                <DeviceBreakedown compliancesInfo={selectedGroupData.compliancesInfo} groupId={groupID} />
              )}
            </div>

            {/* Device Health & Patch Severity */}
            <div className="mt-[20px] flex gap-[20px]">
              {selectedGroupData.deviceHealth && (
                <DeviceHealth deviceHealth={selectedGroupData.deviceHealth} groupId={groupID ?? undefined} />
              )}
              {selectedGroupData.patchBySeverity && (
                <PachBySeverity
                  patchBySeverity={selectedGroupData.patchBySeverity}
                  groupId={groupID}
                  severityOrder={patchBySeverityConfig.severityCVEMapping?.map((entry) => entry.severity) ?? []}
                  durationOrder={patchBySeverityConfig.daysRange ?? []}
                />
              )}
            </div>

            {/* Critical Severity Patches */}
            {<GridPage criticalSeverityPatches={selectedGroupData.criticalSeverityPatches} />}

            {/* Other Components */}
            <Combo endpointsByOs={selectedGroupData.endpointsByOs} groupId={groupID} />
          </>
        ) : (
          <div className="text-gray-500 text-center mt-6">No data available.</div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
